import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import {
  AlertComponent,
  BadgeComponent,
  ButtonCloseDirective,
  ButtonDirective,
  CardBodyComponent,
  CardComponent,
  CardHeaderComponent,
  ColComponent,
  ContainerComponent,
  FormLabelDirective,
  FormSelectDirective,
  ModalBodyComponent,
  ModalComponent,
  ModalFooterComponent,
  ModalHeaderComponent,
  PageItemDirective,
  PageLinkDirective,
  PaginationComponent,
  RowComponent,
  TableDirective
} from '@coreui/angular-pro';
import { IconDirective } from '@coreui/icons-angular';
import { UserPayout, UserPayoutFilters, UserPayoutInfo, UserPayoutService, UserPayoutStatus } from 'app';
import { I18nService } from 'i18n';
import { Logger } from 'shared';

@Component({
  selector: 'app-user-payouts-page',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ContainerComponent,
    RowComponent,
    ColComponent,
    CardComponent,
    CardHeaderComponent,
    CardBodyComponent,
    ButtonDirective,
    TableDirective,
    BadgeComponent,
    PaginationComponent,
    PageItemDirective,
    PageLinkDirective,
    AlertComponent,
    ModalComponent,
    ModalHeaderComponent,
    ModalBodyComponent,
    ModalFooterComponent,
    ButtonCloseDirective,
    IconDirective,
    FormSelectDirective,
    FormLabelDirective
  ],
  templateUrl: './user-payouts-page.component.html',
  styleUrls: ['./user-payouts-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UserPayoutsPageComponent {
  private logger = new Logger('UserPayoutsPageComponent');
  private userPayoutService = inject(UserPayoutService);
  private router = inject(Router);
  private i18n = inject(I18nService);
  private destroyRef = inject(DestroyRef);

  // ────────────────────────────────────────────────
  // State signals
  // ────────────────────────────────────────────────
  payouts = signal<UserPayout[]>([]);
  loading = signal(false);

  filters = signal<UserPayoutFilters>({
    page: 1,
    per_page: 20
  });

  totalPages = signal(0);
  totalCount = signal(0);
  totalAmount = signal(0);
  totalFee = signal(0);

  payoutInfo = signal<UserPayoutInfo | null>(null);
  loadingInfo = signal(false);

  // Detail modal
  showDetailModal = signal(false);
  selectedPayout = signal<UserPayout | null>(null);
  loadingDetail = signal(false);

  // Messages
  error = signal<string | null>(null);
  success = signal<string | null>(null);

  // Options
  statusOptions = signal<UserPayoutStatus[]>(['requested', 'pending', 'completed', 'cancelled']);
  perPageOptions = [10, 20, 50];

  // ────────────────────────────────────────────────
  // Computed pagination
  // ────────────────────────────────────────────────
  visiblePages = computed(() => {
    const current = this.filters().page || 1;
    const total = this.totalPages();
    const maxVisible = 5;
    const pages: number[] = [];

    if (total <= maxVisible) {
      for (let i = 1; i <= total; i++) pages.push(i);
      return pages;
    }

    let start = Math.max(1, current - 2);
    let end = Math.min(total, start + maxVisible - 1);
    if (end - start < maxVisible - 1) {
      start = Math.max(1, end - maxVisible + 1);
    }
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  });

  showingStart = computed(() => {
    const f = this.filters();
    return ((f.page || 1) - 1) * (f.per_page || 20) + 1;
  });

  showingEnd = computed(() => {
    const f = this.filters();
    const end = (f.page || 1) * (f.per_page || 20);
    return Math.min(end, this.totalCount());
  });

  // ────────────────────────────────────────────────
  // Effects
  // ────────────────────────────────────────────────
  constructor() {
    // Load initial data
    this.loadPayoutInfo();
    this.loadPayouts();

    // Reload payouts when filters change
    effect(() => {
      this.filters();
      this.loadPayouts();
    });
  }

  // ────────────────────────────────────────────────
  // Core actions
  // ────────────────────────────────────────────────
  private loadPayoutInfo(): void {
    this.loadingInfo.set(true);
    this.userPayoutService.getPayoutInfo()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
      next: info => {
        this.payoutInfo.set(info);
        this.loadingInfo.set(false);
      },
      error: err => {
        this.logger.error('Failed to load payout info:', err);
        this.loadingInfo.set(false);
      }
    });
  }

  private loadPayouts(): void {
    this.loading.set(true);
    this.userPayoutService.getPayouts(this.filters())
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: response => {
            this.payouts.set(response.payouts);
            this.totalPages.set(response.total_pages);
            this.totalCount.set(response.total_count);
            this.totalAmount.set(response.total_amount);
            this.totalFee.set(response.total_fee);
            this.loading.set(false);
          },
          error: err => {
            this.logger.error('Failed to load payouts:', err);
            this.loading.set(false);
            this.error.set('Failed to load payouts');
          }
        });
  }

  updateFilterProperty<K extends keyof UserPayoutFilters>(
    key: K,
    value: UserPayoutFilters[K] | null | undefined
  ): void {
    this.filters.update(current => {
      const newValue = value === '' || value == null ? undefined : value;
      return {
        ...current,
        [key]: newValue,
        page: 1
      };
    });
  }

  applyFilters(): void {
    this.filters.update(f => ({ ...f, page: 1 }));
  }

  resetFilters(): void {
    this.filters.set({
      page: 1,
      per_page: 20
    });
  }

  changePage(page: number): void {
    if (page < 1 || page > this.totalPages()) return;
    this.filters.update(f => ({ ...f, page }));
  }

  changePerPage(): void {
    this.filters.update(f => ({ ...f, page: 1 }));
  }

  navigateToNewPayout(): void {
    const lang = this.i18n.getLanguage();
    this.router.navigate(['/', lang, 'my-payouts', 'request']);
  }

  // ────────────────────────────────────────────────
  // Detail Modal
  // ────────────────────────────────────────────────
  openDetailModal(payout: UserPayout): void {
    this.selectedPayout.set(null);
    this.loadingDetail.set(true);
    this.showDetailModal.set(true);

    this.userPayoutService.getPayout(payout.id)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: response => {
            this.selectedPayout.set(response.payout);
            this.loadingDetail.set(false);
          },
          error: err => {
            this.logger.error('Failed to load payout detail:', err);
            this.loadingDetail.set(false);
            this.selectedPayout.set(payout); // fallback to basic data
          }
        });
  }

  closeDetailModal(): void {
    this.showDetailModal.set(false);
    this.selectedPayout.set(null);
  }

  cancelPayout(payout: UserPayout): void {
    if (!confirm('Are you sure you want to cancel this payout request?')) return;

    this.userPayoutService.cancelPayout(payout.id)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: response => {
            this.success.set(response.message || 'Payout cancelled successfully');
            this.loadPayouts();
            this.loadPayoutInfo();
            this.closeDetailModal();
            setTimeout(() => this.success.set(null), 3000);
          },
          error: err => {
            this.error.set(err.error?.message || err.error?.error || 'Failed to cancel payout');
            setTimeout(() => this.error.set(null), 5000);
          }
        });
  }

  // ────────────────────────────────────────────────
  // UI Helpers
  // ────────────────────────────────────────────────
  getStatusBadgeClass(status: string): string {
    switch (status) {
      case 'requested': return 'warning';
      case 'pending': return 'info';
      case 'completed': return 'success';
      case 'cancelled': return 'danger';
      default: return 'secondary';
    }
  }

  formatDate(dateString?: string): string {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  formatAmount(amount?: number): string {
    return amount != null ? amount.toFixed(2) : '-';
  }
}